import { useInfiniteQuery, type QueryKey, type InfiniteData } from "@tanstack/react-query";
import { apiFetch, type ApiFetchOptions } from "./apiFetch.ts";

export interface ApiCursorPage<T, C> {
  items: T[];
  nextCursor: C | null;
}

export interface UseInfiniteApiQueryOptions<T, C> {
  queryKey: QueryKey;
  buildUrl: (cursor: C | null, limit: number) => string;
  selectPage: (raw: unknown) => ApiCursorPage<T, C>;
  pageSize?: number;
  enabled?: boolean;
  fetchOptions?: ApiFetchOptions;
}

export interface UseInfiniteApiQueryResult<T> {
  items: T[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const DEFAULT_PAGE_SIZE = 50;

export function useInfiniteApiQuery<T, C = unknown>(
  options: UseInfiniteApiQueryOptions<T, C>,
): UseInfiniteApiQueryResult<T> {
  const {
    queryKey,
    buildUrl,
    selectPage,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled,
    fetchOptions,
  } = options;

  const query = useInfiniteQuery<
    ApiCursorPage<T, C>,
    Error,
    InfiniteData<ApiCursorPage<T, C>>,
    QueryKey,
    C | null
  >({
    queryKey,
    enabled,
    initialPageParam: null,
    queryFn: async ({ pageParam, signal }) => {
      const raw = await apiFetch(buildUrl(pageParam as C | null, pageSize), {
        ...fetchOptions,
        signal,
      });
      return selectPage(raw);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  return {
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
